import { createContext, useContext, type JSX } from 'react';

import { CONTENT_MANAGER_STEPS } from 'components/ExperienceCanvas/types';
import { ContentStateItem } from 'store/content/content.type';
import { JourneyComponentTypes } from '../types/flow-builder.types';
import { PushPayloadType } from 'interface/push/push.interface';
import { PushStateItem } from 'store/push/push.type';
import { RuleStateItem } from 'store/rule/rule.type';
import { TSlidingSidePanelProps } from 'components/ExperienceCanvas/types';
import { WebhookStateItem } from 'store/webhook/webhook.type';
import { SMSStateItem } from 'store/sms/sms.type';
import { EmailStateItem } from 'store/email/email.type';
import { EntityRef } from 'interface/content/content.interface';

export type RenameModalData = {
  stepIndex: number;
  entity: ContentStateItem | PushStateItem | RuleStateItem | EmailStateItem | SMSStateItem | null;
  entityType: JourneyComponentTypes;
  entityNodeName?: string;
  // ToDo: Re-factor RenameModalData type to encapsulate all types of entity props
  pushType?: PushPayloadType;
};

export type TContentSlideoutProps = {
  isOpen: boolean;
  contentRefId?: string;
  startingStep?: CONTENT_MANAGER_STEPS;
  headerInfo: Partial<TSlidingSidePanelProps['headerInfo']>;
};

export type TPushSlideoutProps = {
  isOpen: boolean;
  iconOverride?: JSX.Element;
  titleOverride?: string;
};

export type TAudienceSlideoutProps = {
  isOpen: boolean;
  isViewOnlyMode: boolean;
};

export type TWebhookSlideoutProps = {
  isOpen: boolean;
  isViewOnly?: boolean;
  webhook: WebhookStateItem;
};

export type TSMSSlideoutProps = {
  isOpen: boolean;
  sms: SMSStateItem;
  isViewOnly?: boolean;
};

export type TEmailSlideoutProps = {
  isOpen: boolean;
  email: EmailStateItem;
  isViewOnly?: boolean;
};

export type TIsBeingUsedProps = {
  isOpen: boolean;
  referencedBy?: EntityRef[];
  primaryAction?: () => void;
  secondaryAction?: () => void;
};

export type FlowBuilderShellVariant = 'sidePanel' | 'modal';

export type TFlowBuilderContext = {
  shellVariant: FlowBuilderShellVariant;
  modals: {
    rename: {
      data: RenameModalData | null;
      setData: React.Dispatch<React.SetStateAction<RenameModalData | null>>;
    };
    isBeingUsed: {
      data: TIsBeingUsedProps | null;
      setData: React.Dispatch<React.SetStateAction<TIsBeingUsedProps | null>>;
    };
  };
  sidePanels: {
    // ToDo: Merge the types later and add a sidepanel type to distinguish
    targetAudience: TAudienceSlideoutProps & {
      isOpen: boolean;
      setAudienceContext: React.Dispatch<React.SetStateAction<TAudienceSlideoutProps>>;
      setStepIndex: React.Dispatch<React.SetStateAction<number>>;
      stepIndex: number;
    };
    schedule: {
      isOpen: boolean;
      setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
      setStepIndex: React.Dispatch<React.SetStateAction<number>>;
      stepIndex: number;
    };
    push: TPushSlideoutProps & {
      isOpen: boolean;
      setPushContext: React.Dispatch<React.SetStateAction<TPushSlideoutProps>>;
      setStepIndex: React.Dispatch<React.SetStateAction<number>>;
      stepIndex: number;
    };
    content: TContentSlideoutProps & {
      isOpen: boolean;
      setContentContext: React.Dispatch<React.SetStateAction<TContentSlideoutProps>>;
      setStepIndex: React.Dispatch<React.SetStateAction<number>>;
      stepIndex: number;
    };
    webhook: TWebhookSlideoutProps & {
      isOpen: boolean;
      setWebhookContext: React.Dispatch<React.SetStateAction<TWebhookSlideoutProps>>;
      setStepIndex: React.Dispatch<React.SetStateAction<number>>;
      stepIndex: number;
    };
    sms: TSMSSlideoutProps & {
      isOpen: boolean;
      setSMSContext: React.Dispatch<React.SetStateAction<TSMSSlideoutProps>>;
      setStepIndex: React.Dispatch<React.SetStateAction<number>>;
      stepIndex: number;
    };
    email: TEmailSlideoutProps & {
      isOpen: boolean;
      setEmailContext: React.Dispatch<React.SetStateAction<TEmailSlideoutProps>>;
      setStepIndex: React.Dispatch<React.SetStateAction<number>>;
      stepIndex: number;
    };
  };
};

const FlowBuilderContext = createContext<TFlowBuilderContext | null>(null);

const useFlowBuilderContext = () => {
  const context = useContext(FlowBuilderContext);

  if (!context) {
    throw new Error('useFlowBuilderContext must be used within FlowBuilderContextProvider');
  }

  return context;
};

export { FlowBuilderContext, useFlowBuilderContext };
